import React, { FunctionComponent } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NP_TEST_PLAN_INFORMANT_ORDER, NpTestPlanMeasureSection, sumDuration } from '../data/npTestPlanMeasureUtils'
import { useNpTestPlanMeasureLabels } from '../hooks/useNpTestPlanMeasureLabels'

export interface NpTestPlanTimeSummaryProps {
  sections: NpTestPlanMeasureSection[]
}

const Summary = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const Group = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['4px'],
}))

const Row = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing['8px'],
}))

export const NpTestPlanTimeSummary: FunctionComponent<NpTestPlanTimeSummaryProps> = ({ sections }) => {
  const { colors } = useTheme() as ThemeType
  const { formatDuration, formatInformant, formatMeasureType } = useNpTestPlanMeasureLabels()

  return (
    <Summary testID={tID('NpTestPlanTimeSummary')}>
      {sections.map(({ measureType, groups }) => (
        <Group key={measureType}>
          <BodyText bold size={BodyTextSize.SMALL} text={formatMeasureType(measureType)} />
          {NP_TEST_PLAN_INFORMANT_ORDER.map((informant) => {
            // Every copy of an informant's group counts towards that informant's time.
            const measures = groups.filter((group) => group.informant === informant).flatMap((group) => group.measures)
            return (
              measures.length > 0 && (
                <Row key={informant} testID={tID(`NpTestPlanTimeSummary-${measureType}-${informant}`)}>
                  <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text={formatInformant(informant)} />
                  <BodyText
                    size={BodyTextSize.SMALL}
                    color={colors.textSecondary}
                    text={formatDuration(sumDuration(measures))}
                  />
                </Row>
              )
            )
          })}
        </Group>
      ))}
    </Summary>
  )
}
